import {ProtocolClient} from './ProtocolClient.ts'
import {PlayerEnums} from '../../../shared/GameEnums.ts'
import {RoomClientState} from './RoomClientState.ts'
import {RoomCreationPhase, RoomPlayOrderSelectionData, RoomSeedOrderSelectionData} from '@colonist/lobby-types'
import {PlayerColor} from '@colonist/game-states'
import {COLONIST_VERSION, DiceSettingEnum, MapSettingEnum, ModeSettingEnum, UserSessionId} from '@colonist/types'
import {BotDifficulty, BotSpeedSettings, GameSpeedSettings} from '@colonist/game-base-types'
import {
    RoomBotDifficulty,
    RoomBotSpeed, RoomChatResponse, RoomChatSendFailReason,
    RoomCommand,
    RoomDiceType,
    RoomEvent,
    RoomGameMode,
    RoomGameSpeed,
    RoomInfoMessageType,
    RoomMap,
    RoomPlayerColor, RoomProtocolCreationPhase,
} from '@colonist/protocol-lobby'
import RoomInfoMessage = PlayerEnums.RoomInfoMessage
import {RoomSessionClientState} from './RoomSessionClientState.ts'

export class RoomProtocolMapperClient {
    private readonly protocol: ProtocolClient<RoomEvent, RoomCommand>

    constructor(protocol: ProtocolClient<RoomEvent, RoomCommand>) {
        this.protocol = protocol
    }

    onRoomStateUpdated(handler: (roomState: RoomClientState) => void) {
        this.protocol.onEvent('StateUpdated', (state) => {
            const sessions: RoomSessionClientState[] = state.sessions.map((session) => ({
                roomSessionId: session.roomSessionId,
                userSessionId: session.userSessionId,
                userId: session.userId,
                isBot: session.isBot,
                isReadyToPlay: session.isReadyToPlay,
                selectedColor: protocolPlayerColorMapping[session.selectedColor],
                username: session.username,
                isMember: session.isMember,
                icon: session.icon,
                profilePictureUrl: session.profilePictureUrl,
                karmaCompletedGames: session.karmaCompletedGames,
                karmaTotalGames: session.karmaTotalGames,
                availableColors: session.availableColors.map((availableColor) => protocolPlayerColorMapping[availableColor]),
                botDifficulty: session.botDifficulty == undefined ? undefined : protocolBotDifficultyMapping[session.botDifficulty],
            }))

            handler(RoomClientState.deserialize({
                updateSequence: state.updateSequence,
                gameSetting: {
                    id: state.roomId,
                    privateGame: state.private,
                    playOrderSelectionActive: state.playOrderSelectionActive,
                    minimumKarma: state.minimumKarma,
                    modeSetting: protocolGameModeMapping[state.gameMode],
                    mapSetting: protocolMapMapping[state.map],
                    diceSetting: protocolDiceTypeMapping[state.diceType],
                    victoryPointsToWin: state.victoryPointsToWin,
                    cardDiscardLimit: state.cardDiscardLimit,
                    maxPlayers: state.maxPlayers,
                    gameSpeed: protocolGameSpeedMapping[state.gameSpeed],
                    botSpeed: protocolBotSpeedMapping[state.botSpeed],
                    hideBankCards: state.hiddenBankCards,
                    friendlyRobber: state.friendlyRobber,
                },
                isTournament: state.isTournament,
                isTestFreeExpansionsAndMaps: state.isTestFreeExpansionsAndMaps,
                victoryPointsRecommendedLimit: state.victoryPointsRecommendedLimit,
                victoryPointsMaxAllowed: state.victoryPointsMaxAllowed,
                kickedUserIds: state.kickedUserIds,
                creationPhase: protocolCreationPhaseDifficultyMapping[state.creationPhase],
                hostSession: sessions[0],
                guests: sessions.slice(1),
            }))
        })
    }

    onInfoMessageUpdated(handler: (data: PlayerEnums.RoomInfoMessage) => void) {
        this.protocol.onEvent('InfoMessageUpdated', ({messageType}) => {
            handler(infoMessageMapping[messageType])
        })
    }

    onSeedOrderSelected(handler: (seedOrderData: RoomSeedOrderSelectionData) => void) {
        this.protocol.onEvent('SeedOrderSelected', ({seedOrder}) => {
            handler({seedOrder: seedOrder.map((value) => [value[0], protocolPlayerColorMapping[value[1]]])})
        })
    }

    onPlayOrderSelected(handler: (data: RoomPlayOrderSelectionData) => void) {
        this.protocol.onEvent('PlayOrderSelected', ({mapState, currentSelector, playOrder}) => {
            handler({
                mapState: mapState,
                currentSelector: protocolPlayerColorMapping[currentSelector],
                playOrder: playOrder.map((value) => [protocolPlayerColorMapping[value[0]], value[1]]),
            })
        })
    }

    onGotKicked(handler: (roomId: string, canRejoin: boolean) => void) {
        this.protocol.onEvent('GotKicked', ({roomId, canRejoin}) => {
            handler(roomId, canRejoin)
        })
    }

    onFailedToStartGameMembershipRequired(handler: () => void, removeExisting: boolean = false) {
        if(removeExisting) this.protocol.removeListener('FailedToStartGameMembershipRequired')
        this.protocol.onEvent('FailedToStartGameMembershipRequired', handler)
    }

    onReceivedChatMessage(handler: (message: string, senderSessionId: UserSessionId) => void) {
        this.protocol.onEvent('ReceivedChatMessage', ({message, senderSessionId}) => {
            handler(message, senderSessionId as UserSessionId)
        })
    }

    onInvalidPlayerCountEncountered(handler: (playerCount: number, missingExtension: boolean) => void) {
        this.protocol.onEvent('InvalidPlayerCountEncountered', ({playerCount, missingExtension}) => {
            handler(playerCount, missingExtension)
        })
    }

    onFailedToReducePlayerCount(handler: () => void) {
        this.protocol.onEvent('FailedToReducePlayerCount', () => {
            handler()
        })
    }

    onFailedToStartGameBecauseServerIsUpdating(handler: () => void) {
        this.protocol.onEvent('FailedToStartGameBecauseServerIsUpdating', () => {
            handler()
        })
    }

    onFailedToStartGamePlayersNotReady(handler: (players: string[]) => void) {
        this.protocol.onEvent('FailedToStartGamePlayersNotReady', ({players}) => {
            handler(players)
        })
    }

    onFailedToSelectUsedColor(handler: () => void) {
        this.protocol.onEvent('FailedToSelectUsedColor', () => {
            handler()
        })
    }

    onFailedToExecuteCommandGameStarting(handler: () => void) {
        this.protocol.onEvent('FailedToExecuteCommandGameStarting', () => {
            handler()
        })
    }

    onFailedToSetGameSpeed(handler: () => void) {
        this.protocol.onEvent('FailedToSetGameSpeed', () => {
            handler()
        })
    }

    onKickedInactiveHost(handler: () => void) {
        this.protocol.onEvent('KickedInactiveHost', () => {
            handler()
        })
    }

    onFailedToSendChatMessage(handler: (reason: RoomChatSendFailReason, mutedUntil: string | undefined) => void) {
        this.protocol.onEvent('FailedToSendChatMessage', ({reason, mutedUntil}) => {
            handler(reason, mutedUntil)
        })
    }

    onReceivedChatResponse(handler: (response: RoomChatResponse) => void) {
        this.protocol.onEvent('ReceivedChatResponse', ({response}) => {
            handler(response)
        })
    }

    setReadyToStart(ready: boolean) {
        this.protocol.send('setReadyToStart', {ready: ready})
    }

    selectColor(roomSessionId: string, color: PlayerColor) {
        this.protocol.send('selectColor', {
            roomSessionId: roomSessionId,
            color: playerColorMapping[color],
        })
    }

    startGame() {
        this.protocol.send('startGame', {clientVersion: COLONIST_VERSION})
    }

    startSeedOrderSelection() {
        this.protocol.send('startSeedOrderSelection', {})
    }

    kickPlayer(roomSessionId: string) {
        this.protocol.send('kickPlayer', {roomSessionId: roomSessionId})
    }

    addBot() {
        this.protocol.send('addBot', {})
    }

    setBotDifficulty(roomSessionId: string, botDifficulty: BotDifficulty) {
        this.protocol.send('setBotDifficulty', {roomSessionId: roomSessionId, botDifficulty: botDifficultyMapping[botDifficulty]})
    }

    setPrivateGame(value: boolean) {
        this.protocol.send('setPrivateGame', {privateGame: value})
    }

    toggleNoTrolls() {
        this.protocol.send('toggleNoTrolls', {})
    }

    togglePlayOrderSelection() {
        this.protocol.send('togglePlayOrderSelection', {})
    }

    setGameMode(value: ModeSettingEnum) {
        this.protocol.send('setGameMode', {gameMode: gameModeMapping[value]})
    }

    setMap(value: MapSettingEnum) {
        this.protocol.send('setMap', {map: mapMapping[value]})
    }

    setDiceType(value: DiceSettingEnum) {
        this.protocol.send('setDiceType', {diceType: diceTypeMapping[value]})
    }

    setVictoryPointsToWin(value: number) {
        this.protocol.send('setVictoryPointsToWin', {victoryPoints: value})
    }

    setGameSpeed(value: GameSpeedSettings) {
        this.protocol.send('setGameSpeed', {gameSpeed: gameSpeedMapping[value]})
    }

    setCardDiscardLimit(value: number) {
        this.protocol.send('setCardDiscardLimit', {limit: value})
    }

    setFriendlyRobber(value: boolean) {
        this.protocol.send('setFriendlyRobber', {friendlyRobber: value})
    }

    setHideBankCards(value: boolean) {
        this.protocol.send('setHiddenBankCards', {hiddenBankCards: value})
    }

    setMaxPlayers(value: number) {
        this.protocol.send('setMaxPlayers', {maxPlayers: value})
    }

    setSeedOrder(color: PlayerColor, order: number) {
        this.protocol.send('setSeedOrder', {color: playerColorMapping[color], order: order})
    }

    setPlayOrder(order: number) {
        this.protocol.send('setPlayOrder', {order: order})
    }

    startPlayOrderSelection() {
        this.protocol.send('startPlayOrderSelection', {})
    }

    leave() {
        this.protocol.send('leave', {})
    }

    submitChatText(text: string) {
        this.protocol.send('submitChatText', {text: text})
    }
}

const playerColorMapping: Record<Exclude<PlayerColor, PlayerColor.None>, RoomPlayerColor> = {
    [PlayerColor.Blue]: 'blue',
    [PlayerColor.Bronze]: 'bronze',
    [PlayerColor.Gold]: 'gold',
    [PlayerColor.Green]: 'green',
    [PlayerColor.MysticBlue]: 'mysticBlue',
    [PlayerColor.Orange]: 'orange',
    [PlayerColor.Pink]: 'pink',
    [PlayerColor.Purple]: 'purple',
    [PlayerColor.Red]: 'red',
    [PlayerColor.Silver]: 'silver',
    [PlayerColor.White]: 'white',
    [PlayerColor.Black]: 'black',
} as const

const protocolPlayerColorMapping: Record<RoomPlayerColor, PlayerColor> = {
    blue: PlayerColor.Blue,
    bronze: PlayerColor.Bronze,
    gold: PlayerColor.Gold,
    green: PlayerColor.Green,
    mysticBlue: PlayerColor.MysticBlue,
    orange: PlayerColor.Orange,
    pink: PlayerColor.Pink,
    purple: PlayerColor.Purple,
    red: PlayerColor.Red,
    silver: PlayerColor.Silver,
    white: PlayerColor.White,
    black: PlayerColor.Black,
} as const

export const gameModeMapping: Record<ModeSettingEnum, RoomGameMode> = {
    [ModeSettingEnum.Classic4P]: 'classic4P',
    [ModeSettingEnum.TutorialClassic4P]: 'tutorialClassic4P',
    [ModeSettingEnum.Classic56P]: 'classic56P',
    [ModeSettingEnum.Classic78P]: 'classic78P',
    [ModeSettingEnum.Seafarers4P]: 'seafarers4P',
    [ModeSettingEnum.Seafarers56P]: 'seafarers56P',
    [ModeSettingEnum.CitiesAndKnights4P]: 'citiesAndKnights4P',
    [ModeSettingEnum.CitiesAndKnights56P]: 'citiesAndKnights56P',
    [ModeSettingEnum.CitiesAndKnightsSeafarers4P]: 'citiesAndKnightsSeafarers4P',
    [ModeSettingEnum.CitiesAndKnightsSeafarers56P]: 'citiesAndKnightsSeafarers56P',
    [ModeSettingEnum.ServerStressTestClassic4P]: 'serverStressTestClassic4P',
    [ModeSettingEnum.BeginnerModeClassic4P]: 'beginnerModeClassic4P',
    [ModeSettingEnum.ColonistRush4P]: 'colonistRush',
} as const

export const protocolGameModeMapping: Record<RoomGameMode, ModeSettingEnum> = {
    classic4P: ModeSettingEnum.Classic4P,
    tutorialClassic4P: ModeSettingEnum.TutorialClassic4P,
    classic56P: ModeSettingEnum.Classic56P,
    classic78P: ModeSettingEnum.Classic78P,
    seafarers4P: ModeSettingEnum.Seafarers4P,
    seafarers56P: ModeSettingEnum.Seafarers56P,
    citiesAndKnights4P: ModeSettingEnum.CitiesAndKnights4P,
    citiesAndKnights56P: ModeSettingEnum.CitiesAndKnights56P,
    citiesAndKnightsSeafarers4P: ModeSettingEnum.CitiesAndKnightsSeafarers4P,
    citiesAndKnightsSeafarers56P: ModeSettingEnum.CitiesAndKnightsSeafarers56P,
    serverStressTestClassic4P: ModeSettingEnum.ServerStressTestClassic4P,
    beginnerModeClassic4P: ModeSettingEnum.BeginnerModeClassic4P,
    colonistRush: ModeSettingEnum.ColonistRush4P,
} as const

export const mapMapping: Record<MapSettingEnum, RoomMap> = {
    [MapSettingEnum.Classic4P]: 'classic4P',
    [MapSettingEnum.TutorialClassic4P]: 'tutorialClassic4P',
    [MapSettingEnum.Classic4PRandom]: 'classic4PRandom',
    [MapSettingEnum.Classic56P]: 'classic56P',
    [MapSettingEnum.Classic78P]: 'classic78P',
    [MapSettingEnum.SS1HeadingForNewShores3P]: 'sS1HeadingForNewShores3P',
    [MapSettingEnum.SS1HeadingForNewShores4P]: 'sS1HeadingForNewShores4P',
    [MapSettingEnum.SS1HeadingForNewShores56P]: 'sS1HeadingForNewShores56P',
    [MapSettingEnum.TutorialSS1HeadingForNewShores4P]: 'tutorialSS1HeadingForNewShores4P',
    [MapSettingEnum.SS2FourIslands3P]: 'sS2FourIslands3P',
    [MapSettingEnum.SS2FourIslands4P]: 'sS2FourIslands4P',
    [MapSettingEnum.SS2SixIslands56P]: 'sS2SixIslands56P',
    [MapSettingEnum.TutorialSS2FourIslands4P]: 'tutorialSS2FourIslands4P',
    [MapSettingEnum.SS3FogIslands3P]: 'sS3FogIslands3P',
    [MapSettingEnum.SS3FogIslands4P]: 'sS3FogIslands4P',
    [MapSettingEnum.SS3FogIslands56P]: 'sS3FogIslands56P',
    [MapSettingEnum.TutorialSS3FogIsland4P]: 'tutorialSS3FogIsland4P',
    [MapSettingEnum.SS4ThroughTheDesert3P]: 'sS4ThroughTheDesert3P',
    [MapSettingEnum.SS4ThroughTheDesert4P]: 'sS4ThroughTheDesert4P',
    [MapSettingEnum.SS4ThroughTheDesert56P]: 'sS4ThroughTheDesert56P',
    [MapSettingEnum.TutorialSS4ThroughTheDesert4P]: 'tutorialSS4ThroughTheDesert4P',
    [MapSettingEnum.KingOfTheHill]: 'kingOfTheHill',
    [MapSettingEnum.Earth]: 'earth',
    [MapSettingEnum.EarthSeafarers]: 'earthSeafarers',
    [MapSettingEnum.USA]: 'uSA',
    [MapSettingEnum.UK]: 'uK',
    [MapSettingEnum.UKSeafarers]: 'uKSeafarers',
    [MapSettingEnum.Diamond]: 'diamond',
    [MapSettingEnum.Gear]: 'gear',
    [MapSettingEnum.Lakes]: 'lakes',
    [MapSettingEnum.Pond]: 'pond',
    [MapSettingEnum.Twirl]: 'twirl',
    [MapSettingEnum.ShuffleBoard]: 'shuffleBoard',
    [MapSettingEnum.BlackForest]: 'blackForest',
    [MapSettingEnum.Volcano]: 'volcano',
    [MapSettingEnum.Circle]: 'circle',
    [MapSettingEnum.Kite]: 'kite',
    [MapSettingEnum.Romania]: 'romania',
    [MapSettingEnum.MiniBase]: 'miniBase',
    [MapSettingEnum.Monopoly]: 'monopoly',
    [MapSettingEnum.MountainOfRiches]: 'mountainOfRiches',
    [MapSettingEnum.Doggo]: 'doggo',
    [MapSettingEnum.Duel]: 'duel',
    [MapSettingEnum.TradeIslands]: 'tradeIslands',
    [MapSettingEnum.TradeIslandsSeafarers]: 'tradeIslandsSeafarers',
    [MapSettingEnum.Vietnam]: 'vietnam',
    [MapSettingEnum.TurningTidesSeafarers]: 'turningTidesSeafarers',
    [MapSettingEnum.TurningTidesClassic]: 'turningTidesClassic',
    [MapSettingEnum.TwoIsles]: 'twoIsles',
    [MapSettingEnum.JellyFish]: 'jellyFish',
    [MapSettingEnum.GoldRush]: 'goldRush',
    [MapSettingEnum.Istanbul]: 'istanbul',
    [MapSettingEnum.IstanbulSeafarers]: 'istanbulSeafarers',
    [MapSettingEnum.Europe]: 'europe',
    [MapSettingEnum.EuropeSeafarers]: 'europeSeafarers',
    [MapSettingEnum.Stripes]: 'stripes',
    [MapSettingEnum.MonopolyIslands]: 'monopolyIslands',
    [MapSettingEnum.MonopolyIslandsSeafarers]: 'monopolyIslandsSeafarers',
    [MapSettingEnum.CampaignLevel1]: 'campaignLevel1',
    [MapSettingEnum.CampaignLevel2]: 'campaignLevel2',
    [MapSettingEnum.CampaignRoadRace]: 'campaignRoadRace',
    [MapSettingEnum.CampaignRoadRace2]: 'campaignRoadRace2',
} as const

export const protocolMapMapping: Record<RoomMap, MapSettingEnum> = {
    classic4P: MapSettingEnum.Classic4P,
    tutorialClassic4P: MapSettingEnum.TutorialClassic4P,
    classic4PRandom: MapSettingEnum.Classic4PRandom,
    classic56P: MapSettingEnum.Classic56P,
    classic78P: MapSettingEnum.Classic78P,
    sS1HeadingForNewShores3P: MapSettingEnum.SS1HeadingForNewShores3P,
    sS1HeadingForNewShores4P: MapSettingEnum.SS1HeadingForNewShores4P,
    sS1HeadingForNewShores56P: MapSettingEnum.SS1HeadingForNewShores56P,
    tutorialSS1HeadingForNewShores4P: MapSettingEnum.TutorialSS1HeadingForNewShores4P,
    sS2FourIslands3P: MapSettingEnum.SS2FourIslands3P,
    sS2FourIslands4P: MapSettingEnum.SS2FourIslands4P,
    sS2SixIslands56P: MapSettingEnum.SS2SixIslands56P,
    tutorialSS2FourIslands4P: MapSettingEnum.TutorialSS2FourIslands4P,
    sS3FogIslands3P: MapSettingEnum.SS3FogIslands3P,
    sS3FogIslands4P: MapSettingEnum.SS3FogIslands4P,
    sS3FogIslands56P: MapSettingEnum.SS3FogIslands56P,
    tutorialSS3FogIsland4P: MapSettingEnum.TutorialSS3FogIsland4P,
    sS4ThroughTheDesert3P: MapSettingEnum.SS4ThroughTheDesert3P,
    sS4ThroughTheDesert4P: MapSettingEnum.SS4ThroughTheDesert4P,
    sS4ThroughTheDesert56P: MapSettingEnum.SS4ThroughTheDesert56P,
    tutorialSS4ThroughTheDesert4P: MapSettingEnum.TutorialSS4ThroughTheDesert4P,
    kingOfTheHill: MapSettingEnum.KingOfTheHill,
    earth: MapSettingEnum.Earth,
    earthSeafarers: MapSettingEnum.EarthSeafarers,
    uSA: MapSettingEnum.USA,
    uK: MapSettingEnum.UK,
    uKSeafarers: MapSettingEnum.UKSeafarers,
    diamond: MapSettingEnum.Diamond,
    gear: MapSettingEnum.Gear,
    lakes: MapSettingEnum.Lakes,
    pond: MapSettingEnum.Pond,
    twirl: MapSettingEnum.Twirl,
    shuffleBoard: MapSettingEnum.ShuffleBoard,
    blackForest: MapSettingEnum.BlackForest,
    volcano: MapSettingEnum.Volcano,
    circle: MapSettingEnum.Circle,
    kite: MapSettingEnum.Kite,
    romania: MapSettingEnum.Romania,
    miniBase: MapSettingEnum.MiniBase,
    monopoly: MapSettingEnum.Monopoly,
    mountainOfRiches: MapSettingEnum.MountainOfRiches,
    doggo: MapSettingEnum.Doggo,
    duel: MapSettingEnum.Duel,
    tradeIslands: MapSettingEnum.TradeIslands,
    tradeIslandsSeafarers: MapSettingEnum.TradeIslandsSeafarers,
    vietnam: MapSettingEnum.Vietnam,
    turningTidesSeafarers: MapSettingEnum.TurningTidesSeafarers,
    turningTidesClassic: MapSettingEnum.TurningTidesClassic,
    twoIsles: MapSettingEnum.TwoIsles,
    jellyFish: MapSettingEnum.JellyFish,
    goldRush: MapSettingEnum.GoldRush,
    istanbul: MapSettingEnum.Istanbul,
    istanbulSeafarers: MapSettingEnum.IstanbulSeafarers,
    europe: MapSettingEnum.Europe,
    europeSeafarers: MapSettingEnum.EuropeSeafarers,
    stripes: MapSettingEnum.Stripes,
    monopolyIslands: MapSettingEnum.MonopolyIslands,
    monopolyIslandsSeafarers: MapSettingEnum.MonopolyIslandsSeafarers,
    campaignLevel1: MapSettingEnum.CampaignLevel1,
    campaignLevel2: MapSettingEnum.CampaignLevel2,
    campaignRoadRace: MapSettingEnum.CampaignRoadRace,
    campaignRoadRace2: MapSettingEnum.CampaignRoadRace2,
} as const

const diceTypeMapping: Record<DiceSettingEnum, RoomDiceType> = {
    [DiceSettingEnum.Random]: 'random',
    [DiceSettingEnum.Balanced]: 'balanced',
    [DiceSettingEnum.StressTestSequence]: 'stressTestSequence',
    [DiceSettingEnum.PredefinedDiceSequenceDeleted]: 'predefinedDiceSequenceDeleted',
} as const

const protocolDiceTypeMapping: Record<RoomDiceType, DiceSettingEnum> = {
    random: DiceSettingEnum.Random,
    balanced: DiceSettingEnum.Balanced,
    stressTestSequence: DiceSettingEnum.StressTestSequence,
    predefinedDiceSequenceDeleted: DiceSettingEnum.PredefinedDiceSequenceDeleted,
} as const

const gameSpeedMapping: Record<GameSpeedSettings, RoomGameSpeed> = {
    [GameSpeedSettings.Base30s]: 'base30s',
    [GameSpeedSettings.Base60s]: 'base60s',
    [GameSpeedSettings.Base120s]: 'base120s',
    [GameSpeedSettings.Base240s]: 'base240s',
    [GameSpeedSettings.Base12000s]: 'base12000s',
    [GameSpeedSettings.Rush5s]: 'rush5s',
    [GameSpeedSettings.Rush8s]: 'rush8s',
    [GameSpeedSettings.Rush10s]: 'rush10s',
    [GameSpeedSettings.Rush15s]: 'rush15s',
    [GameSpeedSettings.Rush20s]: 'rush20s',
} as const

const protocolGameSpeedMapping: Record<RoomGameSpeed, GameSpeedSettings> = {
    base30s: GameSpeedSettings.Base30s,
    base60s: GameSpeedSettings.Base60s,
    base120s: GameSpeedSettings.Base120s,
    base240s: GameSpeedSettings.Base240s,
    base12000s: GameSpeedSettings.Base12000s,
    rush5s: GameSpeedSettings.Rush5s,
    rush8s: GameSpeedSettings.Rush8s,
    rush10s: GameSpeedSettings.Rush10s,
    rush15s: GameSpeedSettings.Rush15s,
    rush20s: GameSpeedSettings.Rush20s,
} as const

const botSpeedMapping: Record<BotSpeedSettings, RoomBotSpeed> = {
    [BotSpeedSettings.Fast]: 'fast',
    [BotSpeedSettings.Normal]: 'normal',
    [BotSpeedSettings.Slow]: 'slow',
} as const

const protocolBotSpeedMapping: Record<RoomBotSpeed, BotSpeedSettings> = {
    fast: BotSpeedSettings.Fast,
    normal: BotSpeedSettings.Normal,
    slow: BotSpeedSettings.Slow,
} as const

const botDifficultyMapping: Record<BotDifficulty, RoomBotDifficulty> = {
    [BotDifficulty.BotEasy]: 'easy',
    [BotDifficulty.BotMedium]: 'medium',
} as const

const protocolBotDifficultyMapping: Record<RoomBotDifficulty, BotDifficulty> = {
    easy: BotDifficulty.BotEasy,
    medium: BotDifficulty.BotMedium,
} as const

const infoMessageMapping: Record<RoomInfoMessageType, RoomInfoMessage> = {
    none: RoomInfoMessage.None,
    clickReady: RoomInfoMessage.ClickReady,
    premiumItemSelected: RoomInfoMessage.PremiumItemSelected,
} as const

const protocolCreationPhaseDifficultyMapping: Record<RoomProtocolCreationPhase, RoomCreationPhase> = {
    settings: RoomCreationPhase.EditSettings,
    playOrder: RoomCreationPhase.PlayOrderSelection,
    seedOrder: RoomCreationPhase.SeedOrderSelection,
} as const
